import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import Footer from "@/components/Footer";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { storage } from "@/lib/storage";
import { dashboardApi, formatBirthday } from "@/lib/api";
import { 
  LayoutDashboard, Users, MessageSquare, User, Settings, 
  Menu, X, LogOut, Bell, Crown, Sparkles, Calendar, Gift, ChevronRight,
  Plus, MoreHorizontal, UserPlus, Send, CreditCard, Mail, ShoppingBag, Palette
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_401f09c3-dd08-423f-aa93-1a22a3e025e1/artifacts/dr9rvdx1_Color%20logo%20-%20no%20background.png";

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsRead, setNotificationsRead] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [user, setUser] = useState({});
  const [lastReadTimestamp, setLastReadTimestamp] = useState(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const { isPremium, isAdmin } = useSubscription();
  const hasPremiumAccess = isPremium || isAdmin;
  
  // Close FAB menu when route changes
  useEffect(() => {
    setFabOpen(false);
    setMoreMenuOpen(false);
  }, [location.pathname]);
  
  useEffect(() => {
    const loadUser = async () => {
      try {
        const userStr = await storage.getItem("skybday_user");
        if (userStr) {
          setUser(JSON.parse(userStr));
        }
      } catch (e) {
        // Ignore
      }
    };
    loadUser();
  }, []);

  // Load read timestamp from storage on mount
  useEffect(() => {
    const loadReadState = async () => {
      try {
        const lastReadTime = await storage.getItem("notifications_read_time");
        if (lastReadTime) {
          setLastReadTimestamp(lastReadTime);
          // If read today, mark as read
          const today = new Date().toISOString().split('T')[0];
          if (lastReadTime === today) {
            setNotificationsRead(true);
          }
        }
      } catch (e) {
        // Ignore
      }
    };
    loadReadState();
  }, []);

  // Load upcoming events for notifications
  useEffect(() => {
    const loadUpcoming = async () => {
      try {
        const response = await dashboardApi.getUpcoming();
        // Filter to show only events within 7 days
        const urgent = (response.data.upcoming || []).filter(e => e.days_until <= 7);
        setUpcomingEvents(urgent);
        
        // Only reset read status if there are NEW events (days_until = 0 that weren't read today)
        // Check if any event is for TODAY and wasn't already read
        const today = new Date().toISOString().split('T')[0];
        const lastRead = lastReadTimestamp;
        
        if (urgent.some(e => e.days_until === 0) && lastRead !== today) {
          setNotificationsRead(false);
        } else if (lastRead === today) {
          setNotificationsRead(true);
        }
      } catch (e) {
        // Ignore
      }
    };
    loadUpcoming();
  }, [location.pathname, lastReadTimestamp]); // Reload when route changes
  
  // Only show badge count for TODAY's events only (not already read today)
  const urgentCount = notificationsRead ? 0 : upcomingEvents.filter(e => e.days_until === 0).length;
  
  // Mark notifications as read when opening the dropdown
  const handleOpenNotifications = async () => {
    setNotificationsOpen(!notificationsOpen);
    if (!notificationsOpen) {
      // Mark as read when opening and persist the timestamp
      setNotificationsRead(true);
      const today = new Date().toISOString().split('T')[0];
      setLastReadTimestamp(today);
      await storage.setItem("notifications_read_time", today);
    }
  };

  // Main navigation items (shown in bottom bar on mobile)
  const mainNavItems = [
    { path: "/dashboard", label: t('nav.home'), shortLabel: t('nav.home'), icon: <LayoutDashboard className="w-5 h-5" /> },
    { path: "/contacts", label: t('nav.contacts'), shortLabel: t('nav.contacts'), icon: <Users className="w-5 h-5" /> },
    { path: "/cards", label: t('nav.cards'), shortLabel: t('nav.cards'), icon: <Palette className="w-5 h-5" /> },
    { path: "/profile", label: t('nav.profile'), shortLabel: t('nav.profile'), icon: <User className="w-5 h-5" /> },
  ];
  
  // Secondary navigation items (in "More" menu on mobile)
  const moreNavItems = [
    { path: "/message-packs", label: t('nav.templates'), icon: <ShoppingBag className="w-5 h-5" />, badge: t('common.new') || "Nouveau" },
    { path: "/templates", label: t('nav.messages'), icon: <MessageSquare className="w-5 h-5" /> },
    { path: "/settings", label: t('nav.settings'), icon: <Settings className="w-5 h-5" /> },
    // Only show "Passer à Premium" for non-premium users
    ...(!hasPremiumAccess ? [{ 
      path: "/pricing", 
      label: t('common.upgrade'), 
      icon: <Crown className="w-5 h-5 text-amber-500" /> 
    }] : []),
  ];

  // All nav items for desktop sidebar
  const navItems = [
    { path: "/dashboard", label: t('nav.dashboard'), icon: <LayoutDashboard className="w-5 h-5" /> },
    { path: "/contacts", label: t('nav.contacts'), icon: <Users className="w-5 h-5" /> },
    { path: "/cards", label: t('nav.cards'), icon: <Palette className="w-5 h-5" />, badge: t('common.new') || "Nouveau" },
    { path: "/templates", label: t('nav.messages'), icon: <MessageSquare className="w-5 h-5" /> },
    { path: "/message-packs", label: t('nav.templates'), icon: <ShoppingBag className="w-5 h-5" /> },
    // Cadeaux et Cartes Postales temporairement désactivés pour le lancement
    // { path: "/gifts", label: "Cadeaux", icon: <Gift className="w-5 h-5" /> },
    // { path: "/postcards", label: "Cartes Postales", icon: <Mail className="w-5 h-5" /> },
    { path: "/profile", label: t('nav.profile'), icon: <User className="w-5 h-5" /> },
    { path: "/settings", label: t('nav.settings'), icon: <Settings className="w-5 h-5" /> },
  ];
  
  // Add admin link if user is admin
  if (isAdmin) {
    navItems.push({ 
      path: "/admin", 
      label: "Admin", 
      icon: <Crown className="w-5 h-5 text-amber-500" />,
      isAdmin: true 
    });
  }

  const handleLogout = async () => {
    await storage.removeItem("skybday_token");
    await storage.removeItem("skybday_user");
    navigate("/auth");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 bg-card/95 backdrop-blur-xl border-r border-border h-screen fixed left-0 top-0 z-40 flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="SkyBday" className="h-9 w-auto" />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}>
                <motion.div
                  whileHover={{ x: 4 }}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                    ${isActive 
                      ? "bg-primary/10 text-primary border-l-2 border-primary" 
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }
                  `}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                  {item.badge && (
                    <Badge className="ml-auto bg-purple-500/20 text-purple-400 border-purple-500/30 text-[10px] px-1.5">
                      {item.badge}
                    </Badge>
                  )}
                </motion.div>
              </Link>
            );
          })}
        </nav>

        {/* Premium CTA - only show if not premium */}
        {!hasPremiumAccess && (
          <div className="p-4">
            <Link to="/pricing">
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="p-4 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 cursor-pointer"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  <span className="font-semibold text-amber-500">Premium</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Débloquez toutes les fonctionnalités illimitées
                </p>
                <Button size="sm" className="w-full mt-3 rounded-full hero-gradient text-xs">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Passer à Premium
                </Button>
              </motion.div>
            </Link>
          </div>
        )}

        {/* Premium Badge - show if premium (different for admin) */}
        {hasPremiumAccess && (
          <div className="p-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                  {isAdmin ? "Admin Premium" : "Premium"}
                </Badge>
              </div>
              {isAdmin && (
                <p className="text-[10px] text-muted-foreground mt-2">
                  Accès complet gratuit
                </p>
              )}
            </div>
          </div>
        )}

        {/* User Section */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-secondary/50">
            <Avatar className="w-10 h-10">
              <AvatarImage src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`} />
              <AvatarFallback className="bg-primary/20 text-primary">
                {user.name?.charAt(0) || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{user.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user.email}</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="sidebar-logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-64">
        {/* Mobile Header - Fixed at top, covering safe area */}
        <header className="md:hidden fixed top-0 left-0 right-0 z-30 bg-background border-b border-border">
          {/* Safe area spacer - same background as header */}
          <div className="bg-background" style={{ height: 'env(safe-area-inset-top, 0px)' }} />
          <div className="flex items-center justify-between p-3">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="SkyBday" className="h-7 w-auto" />
            </Link>
            <div className="flex items-center gap-1">
              {/* Notifications Bell */}
              <div className="relative">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative h-9 w-9"
                  onClick={handleOpenNotifications}
                >
                  <Bell className="w-5 h-5" />
                  {urgentCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold">
                      {urgentCount > 9 ? "9+" : urgentCount}
                    </span>
                  )}
                </Button>
                
                {/* Notifications Dropdown */}
                <AnimatePresence>
                  {notificationsOpen && (
                    <>
                      {/* Backdrop */}
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setNotificationsOpen(false)} 
                      />
                      
                      <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        className="absolute right-0 top-full mt-2 w-80 max-h-[70vh] overflow-auto bg-card border border-border rounded-xl shadow-xl z-50"
                      >
                        <div className="p-3 border-b border-border sticky top-0 bg-card">
                          <h3 className="font-semibold text-sm flex items-center gap-2">
                            <Bell className="w-4 h-4 text-primary" />
                            Événements à venir
                          </h3>
                        </div>
                        
                        <div className="p-2 space-y-1">
                          {upcomingEvents.length === 0 ? (
                            <div className="text-center py-6 text-muted-foreground text-sm">
                              <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              Aucun événement cette semaine
                            </div>
                          ) : (
                            upcomingEvents.slice(0, 10).map((event) => (
                              <Link
                                key={`${event.type}-${event.id}`}
                                to={event.type === "birthday" ? `/contacts/${event.id}` : `/contacts/${event.contact_id}`}
                                onClick={() => setNotificationsOpen(false)}
                              >
                                <div className={`flex items-center gap-3 p-2.5 rounded-lg hover:bg-secondary/50 transition-colors ${
                                  event.days_until === 0 ? "bg-green-500/10 border border-green-500/30" :
                                  event.days_until <= 1 ? "bg-orange-500/10 border border-orange-500/30" : ""
                                }`}>
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                                    event.type === "birthday" ? "bg-pink-500/20" : "bg-blue-500/20"
                                  }`}>
                                    {event.type === "birthday" ? "🎂" : event.event_icon || "📅"}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{event.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {event.type === "birthday" 
                                        ? `${event.turning_age} ans` 
                                        : event.event_type_name}
                                      {event.days_until === 0 && " • Aujourd'hui !"}
                                      {event.days_until === 1 && " • Demain"}
                                      {event.days_until > 1 && ` • ${event.days_until}j`}
                                    </p>
                                  </div>
                                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                </div>
                              </Link>
                            ))
                          )}
                        </div>
                        
                        <div className="p-2 border-t border-border">
                          <Link to="/dashboard" onClick={() => setNotificationsOpen(false)}>
                            <Button variant="ghost" className="w-full justify-center text-xs h-8">
                              Voir tout le tableau de bord
                            </Button>
                          </Link>
                        </div>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
              
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-border"
            >
              <nav className="p-3 space-y-1">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link 
                      key={item.path} 
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div
                        className={`
                          flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                          ${isActive 
                            ? "bg-primary/10 text-primary" 
                            : "text-muted-foreground hover:bg-secondary"
                          }
                        `}
                      >
                        {item.icon}
                        <span>{item.label}</span>
                      </div>
                    </Link>
                  );
                })}
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-destructive hover:bg-destructive/10 cursor-pointer"
                  onClick={handleLogout}
                >
                  <LogOut className="w-5 h-5" />
                  <span>Déconnexion</span>
                </div>
              </nav>
            </motion.div>
          )}
        </header>

        {/* Page Content - Add top margin on mobile for fixed header */}
        <div className="p-4 md:p-6 lg:p-8 xl:p-12 pb-24 md:pb-12 flex-1 mt-[calc(env(safe-area-inset-top,0px)+56px)] md:mt-0">
          {children}
        </div>

        {/* Footer - Desktop only in main content area */}
        <div className="hidden md:block">
          <Footer />
        </div>
      </main>

      {/* Mobile Bottom Nav - Liquid Glass Design */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 nav-liquid z-50" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}>
        <div className="relative flex items-center justify-between h-16 px-2">
          {/* Left nav items */}
          <div className="flex items-center flex-1 justify-start">
            {mainNavItems.slice(0, 2).map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex flex-col items-center justify-center gap-1 transition-all duration-300 flex-1 py-2 max-w-[70px]
                    ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}
                  `}
                >
                  <motion.div 
                    className={`p-2 rounded-xl transition-all duration-300 ${isActive ? "bg-primary/15 shadow-lg shadow-primary/20" : ""}`}
                    whileTap={{ scale: 0.9 }}
                  >
                    {React.cloneElement(item.icon, { className: `w-5 h-5 transition-transform ${isActive ? "scale-110" : ""}` })}
                  </motion.div>
                  <span className={`text-[10px] font-medium transition-all ${isActive ? "text-primary" : ""}`}>{item.shortLabel}</span>
                </Link>
              );
            })}
          </div>
          
          {/* Center FAB Button - Elevated above nav bar */}
          <div 
            className="absolute left-1/2 -translate-x-1/2"
            style={{ 
              zIndex: fabOpen ? 10001 : 10,
              top: '-6px' // Position above the nav bar
            }}
          >
            <motion.button
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.05 }}
              onClick={() => setFabOpen(!fabOpen)}
              className={`
                w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 shadow-xl
                ${fabOpen 
                  ? "bg-white/20 backdrop-blur-xl text-white rotate-45 border border-white/30 shadow-lg" 
                  : "fab-liquid text-white"
                }
              `}
              aria-label="Menu d'actions"
            >
              <Plus className="w-7 h-7" />
            </motion.button>
          </div>
            
          {/* FAB Menu - Liquid Glass - Rendered outside the nav flow but still inside the nav container */}
          <AnimatePresence mode="wait">
            {fabOpen && (
              <>
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/70 backdrop-blur-md" 
                  style={{ zIndex: 9998 }}
                  onClick={() => setFabOpen(false)} 
                />
                {/* Wrapper div for centering - not animated */}
                <div 
                  className="fixed left-1/2 -translate-x-1/2"
                  style={{ 
                    zIndex: 9999,
                    bottom: 'calc(5.5rem + env(safe-area-inset-bottom, 0px))',
                    width: '280px'
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 50, scale: 0.8 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                    className="shadow-2xl overflow-hidden rounded-2xl border border-white/20"
                    style={{
                      background: 'rgba(25, 25, 30, 0.98)',
                      backdropFilter: 'blur(20px)'
                    }}
                  >
                  <div className="p-4 space-y-2">
                    <motion.button
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.05 }}
                      onClick={() => { navigate("/contacts?action=add"); setFabOpen(false); }}
                      className="flex items-center gap-4 w-full px-4 py-3 rounded-xl hover:bg-white/10 transition-all group"
                    >
                      <div className="p-2.5 bg-gradient-to-br from-blue-500/40 to-blue-600/30 rounded-xl group-hover:scale-110 transition-transform shrink-0">
                        <UserPlus className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="font-medium text-white whitespace-nowrap">Ajouter un contact</span>
                    </motion.button>
                    <motion.button
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.1 }}
                      onClick={() => { navigate("/templates"); setFabOpen(false); }}
                      className="flex items-center gap-4 w-full px-4 py-3 rounded-xl hover:bg-white/10 transition-all group"
                    >
                      <div className="p-2.5 bg-gradient-to-br from-purple-500/40 to-violet-500/30 rounded-xl group-hover:scale-110 transition-transform shrink-0">
                        <MessageSquare className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="font-medium text-white whitespace-nowrap">Messages</span>
                    </motion.button>
                    {/* Cadeaux et Cartes Postales temporairement désactivés pour le lancement */}
                    <motion.button
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.2 }}
                      onClick={() => { navigate("/settings"); setFabOpen(false); }}
                      className="flex items-center gap-4 w-full px-4 py-3 rounded-xl hover:bg-white/10 transition-all group"
                    >
                      <div className="p-2.5 bg-gradient-to-br from-gray-500/40 to-slate-500/30 rounded-xl group-hover:scale-110 transition-transform shrink-0">
                        <Settings className="w-5 h-5 text-gray-400" />
                      </div>
                      <span className="font-medium text-white whitespace-nowrap">Paramètres</span>
                    </motion.button>
                  </div>
                  </motion.div>
                </div>
              </>
            )}
          </AnimatePresence>
          
          {/* Right nav items */}
          <div className="flex items-center flex-1 justify-end">
            {mainNavItems.slice(2, 4).map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex flex-col items-center justify-center gap-1 transition-all duration-300 flex-1 py-2 max-w-[70px]
                    ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}
                `}
              >
                <motion.div 
                  className={`p-2 rounded-xl transition-all duration-300 ${isActive ? "bg-primary/15 shadow-lg shadow-primary/20" : ""}`}
                  whileTap={{ scale: 0.9 }}
                >
                  {React.cloneElement(item.icon, { className: `w-5 h-5 transition-transform ${isActive ? "scale-110" : ""}` })}
                </motion.div>
                <span className={`text-[10px] font-medium transition-all ${isActive ? "text-primary" : ""}`}>{item.shortLabel}</span>
              </Link>
            );
          })}
          </div>
          
          {/* More Menu Button */}
          <div className="relative flex flex-col items-center justify-center gap-1 flex-1 py-2 min-w-[60px]">
            <button
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              className={`flex flex-col items-center gap-1 min-h-[44px] min-w-[44px] touch-manipulation active:scale-95 transition-all duration-300 ${moreMenuOpen ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
              aria-label="Plus d'options"
              aria-expanded={moreMenuOpen}
            >
              <motion.div 
                className={`p-2 rounded-xl transition-all duration-300 ${moreMenuOpen ? "bg-primary/15 shadow-lg shadow-primary/20" : ""}`}
                whileTap={{ scale: 0.9 }}
              >
                <MoreHorizontal className={`w-5 h-5 transition-transform ${moreMenuOpen ? "scale-110" : ""}`} />
              </motion.div>
              <span className={`text-[10px] font-medium ${moreMenuOpen ? "text-primary" : ""}`}>Plus</span>
            </button>
            
            {/* More Menu Dropdown - Liquid Glass */}
            <AnimatePresence>
              {moreMenuOpen && (
                <>
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm" 
                    style={{ zIndex: 9998 }}
                    onClick={() => setMoreMenuOpen(false)} 
                  />
                  {/* Wrapper for centering */}
                  <div 
                    className="fixed left-1/2 -translate-x-1/2"
                    style={{ 
                      zIndex: 9999,
                      bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))',
                      width: '260px'
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 50, scale: 0.8 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 50, scale: 0.8 }}
                      transition={{ type: "spring", stiffness: 400, damping: 25 }}
                      className="glass-liquid shadow-2xl overflow-hidden rounded-2xl"
                    >
                      <div className="p-3 space-y-1">
                        {moreNavItems.map((item, index) => (
                          <motion.div
                            key={item.path}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Link
                              to={item.path}
                              onClick={() => setMoreMenuOpen(false)}
                              className="flex items-center gap-3 px-4 py-3.5 rounded-xl hover:bg-white/10 transition-all min-h-[44px] group"
                            >
                              <span className="group-hover:scale-110 transition-transform">{item.icon}</span>
                              <span className="font-medium text-white">{item.label}</span>
                            </Link>
                          </motion.div>
                        ))}
                        <div className="border-t border-white/20 my-2" />
                        <motion.button
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.2 }}
                          onClick={() => { handleLogout(); setMoreMenuOpen(false); }}
                          className="flex items-center gap-3 w-full px-4 py-3.5 rounded-xl text-red-400 hover:bg-red-500/20 transition-all min-h-[44px] group"
                        >
                          <LogOut className="w-5 h-5 group-hover:scale-110 transition-transform" />
                          <span className="font-medium">Déconnexion</span>
                        </motion.button>
                      </div>
                    </motion.div>
                  </div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Layout;
